import { msalInstance, loginRequest } from "./auth";

const API_BASE  = process.env.REACT_APP_API_BASE  || "/api";
const USE_MOCK  = process.env.REACT_APP_USE_MOCK   === "true";
const FUNC_KEY  = process.env.REACT_APP_FUNCTION_KEY || "";

// Auth header 
async function authHeader() {
  const account = msalInstance.getAllAccounts()[0];
  if (!account) return {};
  try {
    const r = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    // Send the ID token, not the access token. loginRequest only asks for
    // Graph scopes, so the access token is a Graph token and never carries
    // this app's roles - the backend's admin check (/techs) always saw none.
    // The ID token has the roles and email the backend reads.
    return { Authorization: `Bearer ${r.idToken || r.accessToken}` };
  } catch { return {}; }
}

//  Base fetch 
async function apiFetch(path, opts = {}) {
  const ah = await authHeader();
  const headers = {
    "Content-Type": "application/json",
    ...(FUNC_KEY ? { "x-functions-key": FUNC_KEY } : {}),
    ...ah,
    ...opts.headers,
  };
  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    // Endpoints that refuse an action explain why in `message` (or `error`).
    // Raise that, not the raw JSON, so an alert reads like a sentence.
    let msg = body || `HTTP ${res.status}`;
    try {
      const j = JSON.parse(body);
      if (j && (j.message || j.error)) msg = j.message || j.error;
    } catch { /* not JSON - keep the raw text */ }
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

// Reopen a closed case. Deletes ONLY the CLOSED decision - every reviewer
// sign-off and override is kept. This is the counterpart to closeDocument and
// is NOT the same as unlockReview, which clears all decisions.
export async function reopenDocument(documentId, reopenedBy, reason = "") {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/reopen`, {
    method: "POST",
    body: JSON.stringify({ reopenedBy, reason }),
  });
}

// Current user
export async function getMyTechIdentity() {
  return apiFetch("/me/tech-identity").catch(() => ({ techWebId: null }));
}

// Technologist roster for the admin "view as tech" picker. Admin-only on the
// backend; a non-admin gets a 403, which resolves to an empty list so the
// caller can just render nothing.
export async function getTechs() {
  return apiFetch("/techs").catch(() => []);
}

// Concurrent identical requests share one fetch. Several components load
// documents on mount; without this each pays the full backend cost.
const _inflightDocs = new Map();
// Short-lived cache of the resolved documents list. Several pages need the
// same list within seconds of each other (grid -> detail page), and the
// backend call is expensive.
const _docsCache = new Map();   // path -> { at, data }
const _DOCS_TTL = 30000;        // 30 seconds

//  Documents 
export async function getDocuments(filters = {}) {
  if (USE_MOCK) {
    const { mockDocuments } = await import("./mockData");
    let docs = [...mockDocuments];
    if (filters.status && filters.status !== "ALL") docs = docs.filter(d => d.status === filters.status);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      docs = docs.filter(d => d.filename.toLowerCase().includes(q));
    }
    return docs;
  }
  const cleaned = Object.fromEntries(Object.entries(filters).filter(([,v]) => v && v !== "ALL" && v !== ""));
  const p = new URLSearchParams(cleaned).toString();
  const path = `/documents${p ? "?" + p : ""}`;

  const hit = _docsCache.get(path);
  if (hit && (Date.now() - hit.at) < _DOCS_TTL) return hit.data;

  if (_inflightDocs.has(path)) return _inflightDocs.get(path);

  const promise = apiFetch(path)
    .then(data => { _docsCache.set(path, { at: Date.now(), data }); return data; })
    .finally(() => _inflightDocs.delete(path));

  _inflightDocs.set(path, promise);
  return promise;
}

export function clearDocumentsCache() {
  _docsCache.clear();
}

export async function getDocument(id) {
  if (USE_MOCK) {
    const { mockDocuments } = await import("./mockData");
    return mockDocuments.find(d => d.id === id);
  }
  // No single-doc endpoint â€” fetch list and find by id
  const docs = await apiFetch("/documents");
  return docs.find(d => d.id === id) || { id, filename: id, status: "QA_FAILED", qaScore: null, uploadedAt: null };
}

export async function getQAResult(documentId) {
  if (USE_MOCK) {
    const { mockQAResult } = await import("./mockData");
    await new Promise(r => setTimeout(r, 350));
    return mockQAResult;
  }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/qa-results`);
}

export async function getAuditLog(documentId) {
  if (USE_MOCK) {
    const { mockAuditLog } = await import("./mockData");
    return mockAuditLog;
  }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/audit`).catch(() => []);
}

export async function uploadDocument(file, onProgress, uploadedBy = "Unknown") {
  if (USE_MOCK) {
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(r => setTimeout(r, 120));
      onProgress?.(i);
    }
    return { id: file.name, filename: file.name, status: "PROCESSING" };
  }

  const ah = await authHeader();
  const fd = new FormData();
  fd.append("file", file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress?.(Math.round(e.loaded / e.total * 100)); };
    xhr.onload  = () => {
      if (xhr.status === 409) { resolve(JSON.parse(xhr.responseText)); return; }
      xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(new Error(`HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.open("POST", `${API_BASE}/upload`);
    if (FUNC_KEY) xhr.setRequestHeader("x-functions-key", FUNC_KEY);
    Object.entries(ah).forEach(([k,v]) => xhr.setRequestHeader(k, v));
    if (uploadedBy) xhr.setRequestHeader("X-Uploaded-By", uploadedBy);
    xhr.send(fd);
  });
}

//  Batch Upload 
export async function batchUploadDocuments(files, batchName, uploadedBy = "Unknown", onFileProgress, force = false) {
  if (USE_MOCK) {
    const batchId = "mock-batch-" + Date.now();
    return { batch_id: batchId, total: files.length, uploaded: files.length, status: "PROCESSING" };
  }
  const ah = await authHeader();
  const fd = new FormData();
  files.forEach(f => fd.append("files", f));
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = e => { if (e.lengthComputable) onFileProgress?.(Math.round(e.loaded / e.total * 100)); };
    xhr.onload  = () => {
      if (xhr.status === 409) { resolve(JSON.parse(xhr.responseText)); return; }
      xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(new Error(`HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Batch upload failed"));
    xhr.open("POST", `${API_BASE}/batch/upload`);
    if (FUNC_KEY) xhr.setRequestHeader("x-functions-key", FUNC_KEY);
    Object.entries(ah).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.setRequestHeader("X-Uploaded-By", uploadedBy);
    xhr.setRequestHeader("X-Batch-Name", batchName);
    xhr.setRequestHeader("X-Force-Upload", force ? "true" : "false");
    xhr.send(fd);
  });
}

export async function getBatchStatus(batchId) {
  if (USE_MOCK) {
    return { batch_id: batchId, status: "PROCESSING", total: 5, completed: 2, passed: 1, failed: 1, files: [] };
  }
  return apiFetch(`/batch/${encodeURIComponent(batchId)}/status`);
}
// A technologist's own monthly report data: their cases in full plus anonymous
// peer score rows. The backend takes the tech from the sign-in token; techWebId
// only counts for an admin viewing as a tech.
export async function getMyReport(month = "", techWebId = "") {
  const p = new URLSearchParams();
  if (month) p.append("month", month);
  if (techWebId) p.append("techWebId", techWebId);
  const q = p.toString();
  return apiFetch(`/my-report${q ? "?" + q : ""}`);
}

// Monthly report emails (admins). The dry run sends nothing; a real send skips
// anyone already emailed for that month, so it can't double-send.
export async function previewMonthlyEmails(month) {
  return apiFetch(`/my-report/emails?month=${encodeURIComponent(month)}`);
}

export async function sendMonthlyEmails(month) {
  return apiFetch(`/my-report/emails?month=${encodeURIComponent(month)}`, { method: "POST" });
}

export async function sendMonthlyTestEmail(month, testTo) {
  return apiFetch(`/my-report/emails?month=${encodeURIComponent(month)}&testTo=${encodeURIComponent(testTo)}`,
                  { method: "POST" });
}

// ── Users (Super Admins) ─────────────────────────────────────────────────────
export async function getAdminUsers() {
  return apiFetch("/portal-users");
}

export async function setUserRole(userId, role, add) {
  return apiFetch("/portal-users/role", { method: "POST", body: JSON.stringify({ userId, role, add }) });
}

// Re-sends the invitation email to a guest who hasn't accepted yet.
export async function resendInvite(email, name) {
  return apiFetch("/portal-users/resend-invite", { method: "POST", body: JSON.stringify({ email, name }) });
}

// Removes every portal role (sign-in access). The directory account stays.
export async function removeUserAccess(userId) {
  return apiFetch("/portal-users/remove", { method: "POST", body: JSON.stringify({ userId }) });
}

export async function inviteUser(email, name, roles = []) {
  return apiFetch("/portal-users/invite", { method: "POST", body: JSON.stringify({ email, name, roles }) });
}

export async function saveUserTeam(email, team) {
  return apiFetch("/portal-users/profile", { method: "PUT", body: JSON.stringify({ email, team }) });
}

// Monthly report email, per technologist. On unless it's switched off here.
export async function saveUserMonthlyEmail(email, monthlyEmail) {
  return apiFetch("/portal-users/profile", { method: "PUT", body: JSON.stringify({ email, monthlyEmail }) });
}

// Reader (IP) groups: one group per reader, used to filter IP Reporting.
export async function getReaders() {
  return apiFetch("/portal-users/readers");
}

// A group's email is optional: it's where that group's IP reports will go.
export async function setReaderGroupEmail(group, emails) {
  return apiFetch("/portal-users/readers/group-email", { method: "PUT", body: JSON.stringify({ group, emails }) });
}

export async function setReaderGroup(readerId, name, group) {
  return apiFetch("/portal-users/readers/group", { method: "PUT", body: JSON.stringify({ readerId, name, group }) });
}

// The reader's own email: the join between a signed-in physician and their
// reader1_id, and a prerequisite for giving them an account.
export async function setReaderEmail(readerId, name, email) {
  return apiFetch("/portal-users/readers/email", { method: "PUT", body: JSON.stringify({ readerId, name, email }) });
}

export async function setReaderAccount(readerId, name, enabled) {
  return apiFetch("/portal-users/readers/account", { method: "POST", body: JSON.stringify({ readerId, name, enabled }) });
}

// Readers whose access was removed; their guest account may still exist.
export async function getReaderCleanup() {
  return apiFetch("/portal-users/readers/cleanup");
}

export async function resolveReaderCleanup(readerId) {
  return apiFetch("/portal-users/readers/cleanup/resolve", { method: "POST", body: JSON.stringify({ readerId }) });
}

export async function getAuditData() {
  if (USE_MOCK) {
    const { auditMockPayload } = await import("./auditMockData");
    return auditMockPayload;
  }
  return apiFetch("/audit/data");
}

// ── IP Reporting ─────────────────────────────────────────────────────────────
export async function getIPSummary(startDate, endDate, readerIds = [], hospitals = [], groups = [], asReaderId = null) {
  if (USE_MOCK) {
    return {
      period: { startDate, endDate },
      summary: { totalCases: 0, avgTotalReportScore: 0, totalIPs: 0, belowThreshold: 0 },
      chart: [],
      details: [],
      availableReaders: [],
      availableGroups: [],
    };
  }
  const idsParam = (readerIds && readerIds.length)
    ? `&reader1_ids=${encodeURIComponent(readerIds.join(","))}`
    : "";
  // An admin viewing as a physician. The server honours it only for admins,
  // and replaces reader1_ids with it, so the figures are really theirs.
  const asParam = asReaderId ? `&asReaderId=${encodeURIComponent(asReaderId)}` : "";
  // Pipe-separated: hospital names can contain commas.
  const hospParam = (hospitals && hospitals.length)
    ? `&hospitals=${encodeURIComponent(hospitals.join("|"))}`
    : "";
  const groupParam = (groups && groups.length)
    ? `&groups=${encodeURIComponent(groups.join("|"))}`
    : "";
  return apiFetch(
    `/reports/ip-summary?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}${idsParam}${hospParam}${groupParam}${asParam}`
  );
}

export async function getBatchHistory() {
  if (USE_MOCK) return [];
  return apiFetch("/batch/history").catch(() => []);
}
export async function getDashboardStats(serviceRange = "1m", reviewRange = "1m", techWebId = "") {
  if (USE_MOCK) {
    const { mockDashboardStats, mockTrendData, mockCategoryScores } = await import("./mockData");
    return { stats: mockDashboardStats, trend: mockTrendData, categoryScores: mockCategoryScores };
  }
  const _tech = techWebId ? `&techWebId=${encodeURIComponent(techWebId)}` : "";
  return apiFetch(`/dashboard/stats?serviceRange=${serviceRange}&reviewRange=${reviewRange}&tz_offset=${new Date().getTimezoneOffset()}${_tech}`);
}

// Agent
export async function sendAgentMessage(message, documentId = null, history = []) {
  if (USE_MOCK) {
    await new Promise(r => setTimeout(r, 1100));
    const responses = [
      "Based on the indexed QA results, the most recent document failed 3 checks: the closing reader statement was absent, lateral documentation was missing from the digital handshake section, and the anesthesia documentation timestamp exceeded the 15-minute threshold from baseline. The event log itself was complete and chronologically sound.",
      "Reviewing all indexed documents, the most common failure point is the closing reader statement, appearing in 68% of failed documents. The second most common is lateral documentation â€” side of surgery not explicitly noted. I'd recommend adding a template reminder for both at the start of each case.",
      "I found the QA results for this document. Category breakdown: Digital Handshake: 4/5 pts (side of surgery missing), Opening/Closing: 2/8 pts (remote connection statement absent, closing statement absent), Baseline Anesthesia: 4/5 pts (anesthesia documented 23 minutes after baselines), Procedure Documentation: 2/2 pts (full pass), Surgeon Communication: 3/3 pts (full pass).",
      "Across all documents in the index, Digital Handshake checks have a 91% pass rate, while Opening/Closing checks have the lowest rate at 71%. The closing reader statement is the single most frequently failed individual check.",
    ];
    return {
      message: responses[Math.floor(Math.random() * responses.length)],
      sources: documentId
        ? [{ title: `QA: ${documentId}`, document: documentId, type:"qa_result", relevance: 0.92 }]
        : [{ title:"QA Results â€” All Documents", document:"index", type:"qa_result", relevance: 0.88 }]
    };
  }
  return apiFetch("/agent/chat", {
    method: "POST",
    body: JSON.stringify({ message, documentId, history }),
  });
}

// PDF Viewer 
export async function getPdfUrl(documentId) {
  if (USE_MOCK) return { url: null };
  // Use proxy endpoint to avoid CORS issues with blob storage
  const key = FUNC_KEY ? `?code=${FUNC_KEY}` : "";
  return { url: `${API_BASE}/documents/${encodeURIComponent(documentId)}/pdf${key}` };
}

// Page render URL builder — used in QA evidence to display screenshot pages inline.
// Mirrors getPdfUrl's pattern: API_BASE + function key, multi-client safe via .env.
export function getRenderUrl(fileName, pageNum) {
  const key = FUNC_KEY ? `?code=${FUNC_KEY}` : "";
  return `${API_BASE}/documents/${encodeURIComponent(fileName)}/page/${pageNum}/render${key}`;
}

// Reviewer decisions 
export async function getReviewerDecisions(documentId) {
  if (USE_MOCK) return [];
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/review`).catch(() => []);
}

export async function submitReviewerDecision(documentId, ruleId, decision, comment, reviewedBy) {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/review`, {
    method: "POST",
    body: JSON.stringify({ ruleId, decision, comment, reviewedBy }),
  });
}

export async function completeReview(documentId, reviewedBy, summary, auditType = "") {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/complete-review`, {
    method: "POST",
    body: JSON.stringify({ reviewedBy, summary, auditType }),
  });
}

export async function getWaveformAnalysis(documentId) {
  // if (USE_MOCK) {
  //   const { mockWaveformData } = await import("./mockData");
  //   return mockWaveformData;
  // }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/waveforms`).catch(() => null);
}

// ── Waveform QA — per-modality tech summary ──────────────────────────────────
// Powers the individual-tech report's modality treemap. Returns pooled
// per-modality scores per tech. 'General' is excluded server-side.
export async function getWaveformTechSummary(tech = "", from = "", to = "", region = "", techWebId = "", excludeDualTech = false) {
  // if (USE_MOCK) {
  //   const { waveformTechSummaryMock } = await import("./waveformMockData");
  // if (!tech) return waveformTechSummaryMock;
  // // Mirror the endpoint's tech filter so mock and live behave the same.
  // const one = waveformTechSummaryMock.techs[tech];
  //   return {
  //     ...waveformTechSummaryMock,
  //     techs: one ? { [tech]: one } : {},
  //   };
  // }
  const params = new URLSearchParams();
  if (tech) params.append("tech", tech);
  if (from) params.append("from", from);
  if (to)   params.append("to", to);
  if (region && region !== "ALL") params.append("region", region);
  if (techWebId) params.append("techWebId", techWebId);
  if (excludeDualTech) params.append("excludeDualTech", "1");
  const p = params.toString();
  return apiFetch(`/waveform-tech-summary${p ? "?" + p : ""}`);
}


// ── Manual Waveform QA ───────────────────────────────────────────────────────
export async function getWaveformChecks() {
  return apiFetch("/waveform-checks");
}
export async function getWaveformQa(caseId) {
  return apiFetch(`/waveform/${encodeURIComponent(caseId)}`);
}
export async function saveWaveformQa(caseId, payload) {
  // payload: { present_modalities:[...], answers:{id:"yes|no|na"}, values:{id:"text"}, comments:{modality:"note"}, signed?:bool }
  return apiFetch(`/waveform/${encodeURIComponent(caseId)}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function unlockReview(documentId, unlockedBy, auditType = "") {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/unlock-review`, {
    method: "POST",
    body: JSON.stringify({ unlockedBy, auditType }),
  });
}

// Mark a case aborted: a cancelled or abandoned procedure that should never
// have been QA'd. Hides it from the case list and every count. Non-destructive
// and undone by restoreDocument.
export async function abortDocument(documentId, abortedBy, reason = "") {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/abort`, {
    method: "POST",
    body: JSON.stringify({ abortedBy, reason }),
  });
}

export async function restoreDocument(documentId, restoredBy) {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/restore`, {
    method: "POST",
    body: JSON.stringify({ restoredBy }),
  });
}


export async function closeDocument(documentId, closedBy, note = "") {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/close`, {
    method: "POST",
    body: JSON.stringify({ closedBy, note }),
  });
}

export async function flagDocumentAlert(documentId, flaggedBy, action = "flag", note = "") {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true, alert_status: "MANUAL_ALERT", alert_count: 1 }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/alert-flag`, {
    method: "POST",
    body: JSON.stringify({ flaggedBy, action, note }),
  });
}

// Tech flags. A technologist flags one check for a reviewer to look at;
// a reviewer answers it. Advisory only - neither call touches a score or a
// reviewer decision. The backend takes the flagger from the signed-in token,
// so flaggedBy/resolvedBy are only a fallback for display.
export async function addTechFlag(documentId, ruleId, note, flaggedBy) {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/tech-flag`, {
    method: "POST",
    body: JSON.stringify({ ruleId, note, flaggedBy }),
  });
}

export async function resolveTechFlag(documentId, ruleId, note, resolvedBy) {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 200)); return { success: true }; }
  return apiFetch(`/documents/${encodeURIComponent(documentId)}/tech-flag/resolve`, {
    method: "POST",
    body: JSON.stringify({ ruleId, note, resolvedBy }),
  });
}

// Waveform review list. One case per technologist per month, picked by the
// backend and editable by the reviewer.
export async function getWaveformSelection(month = "") {
  if (USE_MOCK) return { month, cases: [], total: 0, signed: 0 };
  const q = month ? `?month=${encodeURIComponent(month)}` : "";
  return apiFetch(`/waveform-selection${q}`).catch(() => ({ month, cases: [], total: 0, signed: 0 }));
}

export async function addToWaveformSelection(caseId, month = "") {
  return apiFetch(`/waveform-selection/${encodeURIComponent(caseId)}`, {
    method: "POST",
    body: JSON.stringify({ month }),
  });
}

export async function removeFromWaveformSelection(caseId, month = "") {
  return apiFetch(`/waveform-selection/${encodeURIComponent(caseId)}`, {
    method: "DELETE",
    body: JSON.stringify({ month }),
  });
}

// Per-File Audit Data 
export async function getFileAuditData(documentIds, auditType = "combined") {
  if (USE_MOCK) {
    return {
      audit_type: auditType,
      documents: documentIds.map(id => ({
        file_name: id, qa_score: 80, pass_count: 12, fail_count: 3, total_checks: 15,
        qa_checks: [], failed_checks: [], alerts: [], alert_status: "NO_ALERT",
        procedure_type: "ACDF", technologist_name: "Mock Tech", surgeon_name: "Mock Surgeon",
      })),
      generated_at: new Date().toISOString()
    };
  }
  return apiFetch("/audit/files", {
    method: "POST",
    body: JSON.stringify({ document_ids: documentIds, audit_type: auditType }),
  });
}

export async function runPidQa(patientId, uploadedBy = "Unknown", force = false) {
  if (USE_MOCK) { await new Promise(r => setTimeout(r, 2000)); return { success: true, patient_id: patientId }; }
  return apiFetch(`/pid/${encodeURIComponent(patientId)}/run-qa`, {
    method: "POST",
    body: JSON.stringify({ uploadedBy, force }),
  });
}

export async function getPidTrackers() {
  if (USE_MOCK) return [];
  return apiFetch("/pid/trackers").catch(() => []);
}

export async function recoverBatch(batchId) {
  return apiFetch(`/batch/${encodeURIComponent(batchId)}/recover`, {
    method: "POST",
  });
}

// Reader ids and names for the "view as" picker. Thinner than
// /portal-users/readers, which is SuperAdmin only and carries emails and
// account state.
export async function getReadersForPicker() {
  return apiFetch("/readers");
}

// Admin QA Checks 
export async function getQAChecks() {
  return apiFetch("/settings/qa-checks");
}

export async function getModalities() {
  return apiFetch("/modalities");
}

export async function saveModalities(modalities) {
  return apiFetch("/modalities", {
    method: "PUT",
    body: JSON.stringify({ modalities }),
  });
}

export async function saveQAChecks(checks, modifiedBy) {
  return apiFetch("/settings/qa-checks", {
    method: "PUT",
    body: JSON.stringify({ checks, modifiedBy }),
  });
}

export async function getQAChecksVersions() {
  return apiFetch("/settings/qa-checks/versions");
}

export async function rollbackQAChecks(versionId) {
  return apiFetch("/settings/qa-checks/rollback", {
    method: "POST",
    body: JSON.stringify({ versionId }),
  });
}

export async function suggestCheckQuestion(question) {
  return apiFetch("/settings/qa-checks/suggest", {
    method: "POST",
    body: JSON.stringify({ question }),
  });
}


// ---------------------------------------------------------------------------
// Manager Sign Off (MSO)
// ---------------------------------------------------------------------------

export async function getMsoData(caseId) {
  return apiFetch(`/mso/${encodeURIComponent(caseId)}`);
}

export async function submitMsoSignoff(caseId, payload) {
  // payload: { decision: 'approved'|'rejected', comments: string, items_acknowledged: string[] }
  return apiFetch(`/mso/${encodeURIComponent(caseId)}/signoff`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Manager Sign Off page: cases for a service-date range, with the status,
// score and both verification dates already projected onto the pointer doc.
export async function getMsoCases(from, to) {
  const params = new URLSearchParams();
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  const p = params.toString();
  return apiFetch(`/mso-cases${p ? "?" + p : ""}`);
}

// One reviewer verifying a run. The server decides whether this is the
// clinical or the final stage from what is already recorded, and refuses the
// same reviewer twice, so the caller does not pass a stage.
export async function verifyMso(caseId, payload) {
  // payload: { notes?: string, version?: number }
  return apiFetch(`/mso/${encodeURIComponent(caseId)}/verify`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

// Every facility the EMR writes, as id/name pairs. The picker needs the id
// because one name can belong to two separate records.
export async function getFacilities() {
  return apiFetch("/facilities");
}

export async function getPriorityFacilities() {
  return apiFetch("/settings/priority-facilities");
}

export async function savePriorityFacilities(facilities) {
  return apiFetch("/settings/priority-facilities", {
    method: "PUT",
    body: JSON.stringify({ facilities }),
  });
}

export async function regenerateMso(caseId) {
  return apiFetch(`/mso/${encodeURIComponent(caseId)}/regenerate`, {
    method: "POST",
  });
}

export const getQACheckOverrideCounts = async () => {
  return apiFetch("/settings/qa-checks/override-counts");
};

export const refineQACheck = async (checkId) => {
  return apiFetch(`/settings/qa-checks/${encodeURIComponent(checkId)}/refine`, {
    method: "POST",
  });
};

export const rerunDocumentQA = async (docId, triggeredBy = "Unknown") => {
  return apiFetch(`/documents/${encodeURIComponent(docId)}/rerun-qa`, {
    method: "POST",
    body: JSON.stringify({ triggeredBy }),
  });
};

// ─── IDR (Independent Dispute Resolution) ───────────────────────────────

export async function getIdrCases(from, to) {
  const params = new URLSearchParams();
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  const p = params.toString();
  return apiFetch(`/idr/cases${p ? "?" + p : ""}`);
}

export async function saveIdrOverrides(patientId, overrides, updatedBy) {
  return apiFetch(`/idr/overrides/${encodeURIComponent(patientId)}`, {
    method: "PUT",
    body: JSON.stringify({ overrides, updatedBy }),
  });
}

// Returns a Blob (PDF for one case, ZIP for many). Not apiFetch — that parses JSON.
export async function generateIdr(caseIds, overrides, preparedBy, preview = false) {
  const ah = await authHeader();
  const headers = {
    "Content-Type": "application/json",
    ...(FUNC_KEY ? { "x-functions-key": FUNC_KEY } : {}),
    ...ah,
  };

  const res = await fetch(`${API_BASE}/idr/generate`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      case_ids: caseIds,
      overrides: overrides || {},
      prepared_by: preparedBy || "",
      preview: !!preview,
    }),
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body && body.error) msg = body.error;
    } catch {
      msg = await res.text().catch(() => msg);
    }
    throw new Error(msg);
  }

  const skipped = res.headers.get("X-IDR-Skipped");
  const ctype = (res.headers.get("Content-Type") || "").toLowerCase();
  // Guard: a 200 that is not a PDF or ZIP means something returned JSON/HTML
  // where a document was expected. Never save that as a .pdf.
  if (!ctype.includes("application/pdf") && !ctype.includes("application/zip")
      && !ctype.includes("octet-stream")) {
    let msg = "The server did not return a document.";
    try { const j = await res.json(); if (j && j.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const blob = await res.blob();

  const disposition = res.headers.get("Content-Disposition") || "";
  let filename = "IDR_document.pdf";
  const marker = "filename=";
  const idx = disposition.indexOf(marker);
  if (idx !== -1) {
    filename = disposition.slice(idx + marker.length).split(";")[0].replace(/"/g, "").trim();
  }

  return { blob, filename, skipped: skipped ? skipped.split(",") : [] };
}

export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// promptOverride previews unsaved wording from the IDR wording editor. It is
// used for this draft only and never stored.
export async function draftIdrSummary(patientId, promptOverride) {
  return apiFetch(`/idr/draft-summary/${encodeURIComponent(patientId)}`, {
    method: "POST",
    ...(promptOverride ? { body: JSON.stringify({ promptOverride }) } : {}),
  });
}

// Admin IDR letter wording
export async function getIdrPrompt() {
  return apiFetch("/settings/idr-prompt");
}

export async function saveIdrPrompt(config, modifiedBy) {
  return apiFetch("/settings/idr-prompt", {
    method: "PUT",
    body: JSON.stringify({ ...config, modifiedBy }),
  });
}

export async function getIdrPromptVersions() {
  return apiFetch("/settings/idr-prompt/versions");
}

export async function rollbackIdrPrompt(versionId, modifiedBy) {
  return apiFetch("/settings/idr-prompt/rollback", {
    method: "POST",
    body: JSON.stringify({ versionId, modifiedBy }),
  });
}

export async function reviewIdrCase(patientId, reviewed, reviewedBy) {
  return apiFetch(`/idr/review/${encodeURIComponent(patientId)}`, {
    method: "POST",
    body: JSON.stringify({ reviewed, reviewedBy: reviewedBy || "" }),
  });
}

export async function getMsoStatusBulk(caseIds) {
  return apiFetch('/mso/status', {
    method: 'POST',
    body: JSON.stringify({ caseIds }),
  });
}

export async function getCaseModalities(caseId) {
  return apiFetch(`/case/${encodeURIComponent(caseId)}/modalities`);
}